import React, { useRef } from "react";

/**
 * Premium 6-box OTP input.
 * - Auto-advances on type, moves back on backspace
 * - Arrow-key navigation
 * - Full-code paste support
 */
const OtpInput = ({ length = 6, value = "", onChange, error, disabled, autoFocus }) => {
  const inputsRef = useRef([]);

  const digits = Array.from({ length }, (_, i) => value[i] || "");

  const focusIndex = (i) => {
    const el = inputsRef.current[i];
    if (el) {
      el.focus();
      el.select?.();
    }
  };

  const setDigit = (index, digit) => {
    const next = digits.slice();
    next[index] = digit;
    onChange(next.join(""));
  };

  const handleChange = (index) => (e) => {
    const raw = e.target.value.replace(/\D/g, "");
    if (!raw) {
      setDigit(index, "");
      return;
    }
    // Typed/auto-filled multiple chars → distribute
    if (raw.length > 1) {
      const chars = raw.slice(0, length - index).split("");
      const next = digits.slice();
      chars.forEach((c, k) => (next[index + k] = c));
      onChange(next.join(""));
      focusIndex(Math.min(index + chars.length, length - 1));
      return;
    }
    setDigit(index, raw);
    if (index < length - 1) focusIndex(index + 1);
  };

  const handleKeyDown = (index) => (e) => {
    if (e.key === "Backspace") {
      e.preventDefault();
      if (digits[index]) {
        setDigit(index, "");
      } else if (index > 0) {
        setDigit(index - 1, "");
        focusIndex(index - 1);
      }
    } else if (e.key === "ArrowLeft" && index > 0) {
      focusIndex(index - 1);
    } else if (e.key === "ArrowRight" && index < length - 1) {
      focusIndex(index + 1);
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, length);
    if (!pasted) return;
    onChange(pasted.padEnd(0));
    focusIndex(Math.min(pasted.length, length - 1));
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between gap-2 sm:gap-2.5" onPaste={handlePaste}>
        {digits.map((d, i) => (
          <input
            key={i}
            ref={(el) => (inputsRef.current[i] = el)}
            type="text"
            inputMode="numeric"
            autoComplete={i === 0 ? "one-time-code" : "off"}
            maxLength={1}
            value={d}
            disabled={disabled}
            autoFocus={autoFocus && i === 0}
            onChange={handleChange(i)}
            onKeyDown={handleKeyDown(i)}
            onFocus={(e) => e.target.select()}
            className={`w-full aspect-square max-w-[52px] text-center text-lg font-semibold rounded border bg-surface_alt text-text_primary outline-none transition-all duration-150
              focus:bg-surface focus:ring-2 focus:ring-primary/[0.12] focus:border-primary/40
              disabled:opacity-55 disabled:cursor-not-allowed
              ${
                error
                  ? "border-danger/60 bg-danger_bg/40 focus:ring-danger/20 focus:border-danger"
                  : d
                  ? "border-primary/40"
                  : "border-border hover:border-border_strong"
              }`}
          />
        ))}
      </div>
      {error && <p className="text-[11.5px] text-danger">{error}</p>}
    </div>
  );
};

export default OtpInput;
